import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit, signal, ViewChild} from '@angular/core';
import {FormControl, FormsModule} from '@angular/forms';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatDialogModule, MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {
  MatListModule,
  MatListOption,
  MatSelectionList,
  MatSelectionListChange
} from '@angular/material/list';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {TranslateModule} from '@ngx-translate/core';
import {sortBy} from 'lodash';
import {
  DialogCloseButtonComponent,
  InfoMessageComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent,
  SelectModule,
  TabPlaceholderComponent,
  TextFieldComponent
} from '@q9elements/ui-kit/components';
import {Q9HtmlEncoderPipe} from '@q9elements/ui-kit/common';
import {UndoService} from '../../../commands/undo.service';
import {RequestsService} from '../../../core/requests/requests.service';
import {MapTreeService} from '../../../core/services/map-tree.service';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../shared/models/diagram-version.constant';
import {DeletedMapFilterPipe} from '../../shared/pipes/deleted-map-filter.pipe';
import {MapFilterPipe} from '../../shared/pipes/map-filter.pipe';

@Component({
  selector: 'app-import-drilldown-dialog',
  templateUrl: './import-drilldown-dialog.component.html',
  styleUrls: ['./import-drilldown-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    FormsModule,
    MatListModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    DeletedMapFilterPipe,
    MapFilterPipe,
    TranslateModule,
    DialogCloseButtonComponent,
    TextFieldComponent,
    SecondaryButtonComponent,
    PrimaryButtonComponent,
    SelectModule,
    InfoMessageComponent,
    Q9HtmlEncoderPipe,
    TabPlaceholderComponent,
    MatIconModule
  ]
})
export class ImportDrilldownDialogComponent implements OnInit {
  @ViewChild('selectionList') matSelectionList: MatSelectionList;

  public error = '';
  public filterControl: FormControl<string>;
  public filterByDeleted = true;
  public importOptions = [
    {formControlName: 'copyFlowlines', text: 'LINKS_TO_MAPS'},
    {formControlName: 'copyRMNodes', text: 'LINKS_TO_REF_MODEL_NODES'},
    {formControlName: 'copyReqStories', text: 'REQUIREMENTS'}
  ];
  public importOptionsValue = {
    copyFlowlines: false,
    copyRMNodes: false,
    copyReqStories: false
  };
  public version: DIAGRAM_VERSION_STATES_ENUM.DRAFT | DIAGRAM_VERSION_STATES_ENUM.MASTER =
    DIAGRAM_VERSION_STATES_ENUM.DRAFT;
  public isLoading = signal(false);
  public mapsForImport!: any;
  public cell!: any;
  public versionsToImport = [
    {
      value: DIAGRAM_VERSION_STATES_ENUM.DRAFT,
      name: 'IMPORT_DD.DRAFT_OPTION'
    },
    {
      value: DIAGRAM_VERSION_STATES_ENUM.MASTER,
      name: 'IMPORT_DD.PUBLISHED_OPTION'
    }
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) protected passedData: any,
    private requestsSvc: RequestsService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private dialogRef: MatDialogRef<ImportDrilldownDialogComponent>,
    private mapTreeSvc: MapTreeService,
    private undoSvc: UndoService
  ) {}

  ngOnInit(): void {
    this.cell = this.passedData.cell;
    this.mapsForImport = sortBy(this.passedData.mapsForImport, map => map.name.toLowerCase());
    this.filterControl = new FormControl('');
  }

  get inputText() {
    return this.filterControl?.value;
  }

  get selectedMap() {
    if (this.matSelectionList && this.matSelectionList.selectedOptions.selected[0]) {
      return this.matSelectionList.selectedOptions.selected[0].value;
    } else {
      return null;
    }
  }

  get taskForImport() {
    return {
      isApi: true,
      method: 'POST',
      url: `/drilldown`,
      data: {
        activityId: Number(this.cell.id),
        diagramVersionId: this.diagramJsonSvc.get().id,
        sourceMapId: this.selectedMap._id,
        version: this.selectedMap.hasMasterVersion
          ? this.version
          : DIAGRAM_VERSION_STATES_ENUM.DRAFT,
        ...this.importOptionsValue
      }
    };
  }

  public changeSelected(event: MatSelectionListChange): void {
    this.matSelectionList.deselectAll();
    const [option] = event.options;

    option?.toggle();
    this.error = '';

    if (!this.selectedMap || !this.selectedMap.hasMasterVersion) {
      this.version = DIAGRAM_VERSION_STATES_ENUM.DRAFT;
    }
  }

  public optionClass(matListOption: MatListOption) {
    return {
      selected: this.optionIsSelected(matListOption),
      'deleted-map': this.mapIsDeleted(matListOption)
    };
  }

  public mapIsDeleted(matListOption: MatListOption) {
    if (matListOption.value) {
      return matListOption.value.state === 'deleted';
    }

    return false;
  }

  public optionIsSelected(matListOption) {
    if (this.selectedMap) {
      return matListOption.value === this.selectedMap;
    } else {
      return false;
    }
  }

  public import() {
    if (!this.selectedMap) {
      this.error = 'POPUPS.NO_SELECTED_DIAGRAM';

      return;
    }

    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.requestsSvc.addToQueue(this.taskForImport, true).then(
      drilldown => {
        this.diagramJsonSvc.update({
          importDrilldown: {activityMxKey: this.cell.id, drilldown: drilldown}
        });
        this.cell.comp.instance.setupFromJson();
        this.isLoading.set(false);
        this.undoSvc.clear();
        this.mapTreeSvc.triggerUpdate();
        this.dialogRef.close();
      },
      error => {
        this.isLoading.set(false);

        switch (error.error.status) {
          case 'import_drilldown.no_rights':
            this.error = 'POPUPS.NO_EDIT_ACCESS';
            break;

          case 'import_drilldown.source_map_not_exist':
            this.error = 'POPUPS.NO_EDIT_DELETED';
            break;
        }

        if (this.matSelectionList) {
          this.matSelectionList.deselectAll();
        }
      }
    );
  }

  public toggleDeletedMaps() {
    if (this.matSelectionList) {
      this.matSelectionList.deselectAll();
    }

    this.filterByDeleted = !this.filterByDeleted;
  }
}
